import { error } from "../../logging/error";
import { info } from "../../logging/info";

import BitmapData = createjs.BitmapData;
import DisplayObject = createjs.DisplayObject;
import Container = createjs.Container;
import MovieClip = createjs.MovieClip;
import Sprite = createjs.Sprite;
import Event = createjs.Event;
import Matrix2D = createjs.Matrix2D;
import Rectangle = createjs.Rectangle;
import {getDefinitionByName} from "@goodgamestudios/createjs-utilities";

import { getLogger } from "@goodgamestudios/logger";
import { AbstractDisplayObjectClip } from "../../rendering/clip/AbstractDisplayObjectClip";

const logger = getLogger("MovieClipHelper");

export class MovieClipHelper {
    public static stopAllMovies(container: Container): void {
        if (!container) {
            return;
        }

        if (container instanceof MovieClip) {
            ((<MovieClip>container)).stop();
        }
        for (let i: number = 0; i < container.numChildren; i++) {
            MovieClipHelper.stopAllMovies((<Container>container.getChildAt(i)));
        }
    }

    public static stopAllMoviesGotoFrameOne(container: Container): void {
        if (!container) {
            return;
        }
        let movie: MovieClip = (<MovieClip>container);
        if (movie && movie.totalFrames > 1) {
            movie.gotoAndStop(1);
        }
        for (let i: number = 0; i < container.numChildren; i++) {
            if (container.getChildAt(i) instanceof MovieClip) {
                MovieClipHelper.stopAllMoviesGotoFrameOne((<Container>container.getChildAt(i)));
            }
        }
    }

    public static playAllMovies(container: Container): void {
        if (!container) {
            return;
        }
        let movie: MovieClip = (<MovieClip>container);
        if (movie && movie.totalFrames > 1) {
            movie.play();
        }
        for (let i: number = 0; i < container.numChildren; i++) {
            if ((<MovieClip>container.getChildAt(i))) {
                MovieClipHelper.playAllMovies((<Container>container.getChildAt(i)));
            }
        }
    }

    /**
     * centerMovieClip
     * scales the MovieClip to the maxSize and centers it
     * */
    public static centerMovieClip(mc: Container, maxHeight: number, maxWidth: number, scaleToFit: boolean = true): void {
        let bounds: Rectangle = mc.getBounds();
        let scaleFactor: number = mc.scaleX;
        if (scaleToFit) {
            scaleFactor = (maxWidth / bounds.width);
            if ((bounds.height * scaleFactor) > maxHeight) {
                scaleFactor = (maxHeight / bounds.height);
            }
        }
        mc.x = -((bounds.width * scaleFactor) / 2 + bounds.left * scaleFactor);
        mc.y = -((bounds.height * scaleFactor) / 2 + bounds.top * scaleFactor);
        mc.scaleX = mc.scaleY = scaleFactor;
    }

    /**
     * isChildrenOf
     * goes through all the parents of a displayObject and checks if the parent is the right one
     * */
    public static isChildrenOf(target: DisplayObject, possibleParent: Container): boolean {
        let tmpDisp: DisplayObject = target;
        let foundIt: boolean = false;
        while (!foundIt && tmpDisp.parent) {
            if (tmpDisp.parent == possibleParent) {
                foundIt = true;
            }
            else {
                tmpDisp = tmpDisp.parent;
            }
        }
        return foundIt;
    }

    public static clearMovieClip(mc: Container): void {
        if (mc) {
            while (mc.numChildren > 0) {
                let c = mc.children[0];
                mc.removeChildAt(0);
                if (c instanceof AbstractDisplayObjectClip) {
                    c.dispose();
                }
            }
        } else {
            info("clearMovieClip called with null mc. Have a nice day debugging.");

        }
    }

    /**
     *  replace an existing icon in an holder
     *  do not replace if iths the same class
     *  delete all children if no new source
     * @param container
     * @param source
     *
     */
    public static replaceMovieClip(container: Container, source: any = null): void {
        if (source) {
            if (container.numChildren > 0) {
                if ((container.getChildAt(0) instanceof source) == false) {
                    MovieClipHelper.clearMovieClip(container);
                    container.addChild(new source());
                }
            }
            else {
                container.addChild(new source());
            }
        }
        else {
            MovieClipHelper.clearMovieClip(container);
        }
    }

    public static disableChildrenExceptList(container: Container, children: any[]): void {
        if (!container) {
            return;
        }
        for (let i: number = 0; i < container.numChildren; i++) {
            let mc: Container = (<Container>container.getChildAt(i));
            for (let child of children) {
                if (mc == child) {
                    mc.mouseEnabled = true;
                }
                else {
                    mc.mouseEnabled = mc.mouseChildren = false;
                }
            }
        }
    }

    public static getMovieClipByClassName(className: string): MovieClip {

        try {
            let dispClass: any = getDefinitionByName(className);
            return new dispClass();
        }
        catch (e) {
            return null;
        }
    }

    public static getSpriteByClassName(className: string): Container {

        try {
            let dispClass: any = getDefinitionByName(className);
            return new (dispClass());
        }
        catch (e) {
            return null;
        }
    }

    /**
     * this method tests if a frameindex or label does exist before it trys to call gotoAndStop or gotoAndPlay.
     * If the frame index is above the total number of frames it starts the goto-methode at the highest possible frame.
     * If the frame index is below 1 or it does not exist as a label it will execute at frame 1.
     *
     * @param movieclip
     *         the Movieclip that wants to use the gotoAndPlay/gotoAndStop methode
     * @param frame
     *         the frame index or label
     * @param play
     *         a boolean to deside if you want to execute gotoAndPlay or gotoAndStop.
     *         It is optional and the default is false.
     */
    public static secureGoto(movieclip: MovieClip, frame: any, play: boolean = false): void {
        let frameIndex: number = 1;
        if (frame.constructor === Number) {
            frameIndex = (<number>frame);
            let totalFrames: number = movieclip.totalFrames;
            if (frameIndex > totalFrames) {
                frameIndex = totalFrames;
            }
            else {
                if (frameIndex <= 0) {
                    frameIndex = 1;
                }
            }
        }
        else {
            // TODO: @HTML5 - createjs doesn't support label in frames
            logger.error("MovieClipHelper.secureGoto: goto frame by label not implemented.");

            // if (frame.constructor === String) {
            //     for (let label of movieclip.currentLabels) {
            //         if (label.name.toLocaleLowerCase() == ((<string>frame )).toLocaleLowerCase()) {
            //             frameIndex = label.frame;
            //         }
            //     }
            //     if (frameIndex <= 0) {
            //         frameIndex = 1;
            //     }
            // }
        }
        if (play) {
            movieclip.gotoAndPlay(frameIndex);
        }
        else {
            movieclip.gotoAndStop(frameIndex);
        }
    }

    /** This Methode returns the visible size of an object.
     *      Methode by Zach Foley
     *         found at http://plasticsturgeon.com/2010/09/as3-get-visible-bounds-of-transparent-display-object/
     * @param source the displayObject of which you want the size
     * @return this rectangle contains the visible height and width of the object, the x and y gives the position of the bounds in the DisplayObject
     *
     */
    public static getVisibleSize(source: DisplayObject): Rectangle {
        if (source.width > 0 && source.height > 0) {
            let rect:Rectangle =  source.getBounds();
            return new Rectangle(rect.x, rect.y, Math.ceil(rect.width), Math.ceil(rect.height));
        }
        else {
            return new Rectangle(0, 0, 0, 0);
        }
    }

    /**
     * Scales a DisplayObject proportionally to fit a given space
     * @param dispObj
     * @param maxWidth
     * @param maxHeight
     *
     */
    public static scaleToFit(dispObj: DisplayObject, maxWidth: number, maxHeight: number): void {
        let fittingScale: number = Math.min(maxWidth / (dispObj.width / dispObj.scaleX), maxHeight / (dispObj.height / dispObj.scaleY));
        dispObj.scaleX = dispObj.scaleY = fittingScale;
    }

    /**
     * Scales a DisplayObject down proportionally to fit a given space if it's larger than that. If it's smaller, it stays the same.
     * @param dispObj
     * @param maxWidth
     * @param maxHeight
     *
     */
    public static scaleDownToFit(dispObj: DisplayObject, maxWidth: number, maxHeight: number): void {
        let fittingScale: number = Math.min(maxWidth / (dispObj.width / dispObj.scaleX), maxHeight / (dispObj.height / dispObj.scaleY));
        if (fittingScale < dispObj.scaleX) {
            dispObj.scaleX = dispObj.scaleY = fittingScale;
        }
    }

    /**
     * Watches the given movieclip until the last frame was reached.
     * After that the callback function is called and the movieclip dispatches a COMPLETE Event
     *
     * @usage
     *     MovieClipHelper.watchAnimationEnd( mc, function(mc:MovieClip) {} )
     *  or you can listen for Event.COMPLETE from the mc
     *
     *
     * @param mc            A MovieClip
     * @param callback        (optional) Callback Function, is called after the last frame with the mc as parameter
     */
    public static watchAnimationEnd(mc: MovieClip, callback: Function = null): void {
        function oef(event: Event): void {
            if (mc.currentFrame == mc.totalFrames) {
                mc.removeEventListener(Event.ENTER_FRAME, this.bindFunction(oef));
                mc.stop();
                mc.dispatchEvent(Event.COMPLETE);
                if (callback instanceof Function) {
                    callback(mc);
                }
            }
        }

        mc.addEventListener(Event.ENTER_FRAME, this.bindFunction(oef));
    }

    public static dissolveDisplayObjectContainer(disp: DisplayObject): void {
        if (!disp) {
            return;
        }

        let removeList: DisplayObject[] = [];
        if (disp instanceof Container) {
            let dispContainer: Container = (<Container>disp);

            for (let i: number = 0; i < dispContainer.numChildren; i++) {
                let child: DisplayObject = dispContainer.getChildAt(i);
                if (child instanceof Container) {
                    MovieClipHelper.dissolveDisplayObjectContainer((<Container>child));
                }
                else {
                    removeList.push(child);
                }
            }

            while (dispContainer.numChildren > 0) {
                dispContainer.removeChildAt(0);
            }

            if (dispContainer.numChildren > 0) {
                error("ERROR");
            }
        }
    }
}
